import { useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { startRegistration } from '@simplewebauthn/browser';
import { api } from '../lib/api.js';
import { dateTime } from '../lib/format.js';
import { Dialog, ErrorState, LoadingState, StatusBadge } from '../components/ui.js';
import '../screens-2a.css';

/**
 * Zabezpečení účtu (D-331): dvoufázové přihlášení aplikací (TOTP), záložní kódy a passkey.
 * Tajný klíč a QR kód se ukážou jen při zapnutí, záložní kódy jen jednou.
 */
interface Security {
  totp: boolean;
  backupCodesLeft: number;
  passkeys: Array<{ id: string; name: string; createdAt: string; lastUsedAt: string | null }>;
  require2faAll: boolean;
  isAdmin: boolean;
}

export function SecurityDialog({
  open,
  onClose,
  forced,
}: {
  open: boolean;
  onClose: () => void;
  /** Správce vyžaduje 2FA: okno se nedá zavřít, dokud není zapnuté. */
  forced?: boolean;
}): JSX.Element {
  const client = useQueryClient();
  const q = useQuery<Security>({
    queryKey: ['profile-security'],
    queryFn: () => api.get('/api/profile/security'),
    enabled: open,
  });
  const [setup, setSetup] = useState<{ secret: string; qrDataUrl: string } | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const refresh = (): void => void client.invalidateQueries({ queryKey: ['profile-security'] });

  const start = useMutation({
    mutationFn: () => api.post<{ secret: string; qrDataUrl: string }>('/api/profile/totp/setup'),
    onSuccess: (r) => setSetup(r),
  });
  const enable = useMutation({
    mutationFn: () => api.post<{ backupCodes: string[] }>('/api/profile/totp/enable', { code }),
    onSuccess: (r) => {
      setSetup(null);
      setCode('');
      setCodes(r.backupCodes);
      refresh();
      void client.invalidateQueries({ queryKey: ['me'] });
    },
  });
  const disable = useMutation({
    mutationFn: () => api.post('/api/profile/totp/disable', { password }),
    onSuccess: () => {
      setPassword('');
      setNote('Dvoufázové přihlášení je vypnuté.');
      refresh();
    },
  });
  const regen = useMutation({
    mutationFn: () =>
      api.post<{ backupCodes: string[] }>('/api/profile/backup-codes', { password }),
    onSuccess: (r) => {
      setPassword('');
      setCodes(r.backupCodes);
      refresh();
    },
  });
  const addKey = useMutation({
    mutationFn: async () => {
      const o = await api.post<{
        options: Parameters<typeof startRegistration>[0]['optionsJSON'];
        challengeId: string;
      }>('/api/profile/passkeys/options');
      const response = await startRegistration({ optionsJSON: o.options });
      return api.post<{ backupCodes: string[] | null }>('/api/profile/passkeys', {
        challengeId: o.challengeId,
        name: name.trim() || 'Passkey',
        response,
      });
    },
    onSuccess: (r) => {
      setName('');
      if (r.backupCodes) setCodes(r.backupCodes);
      setNote('Passkey je přidaný.');
      refresh();
      void client.invalidateQueries({ queryKey: ['me'] });
    },
  });
  const rename = useMutation({
    mutationFn: (v: { id: string; name: string }) =>
      api.patch(`/api/profile/passkeys/${encodeURIComponent(v.id)}`, { name: v.name }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/profile/passkeys/${encodeURIComponent(id)}`),
    onSuccess: refresh,
  });
  const require2fa = useMutation({
    mutationFn: (v: boolean) => api.patch('/api/settings', { require_2fa_all: v }),
    onSuccess: refresh,
  });

  const d = q.data;
  const err = [start, enable, disable, regen, addKey, rename, remove, require2fa].find(
    (m) => m.error,
  )?.error;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Zabezpečení účtu"
      wide
      beforeClose={() => !(forced && d && !d.totp && d.passkeys.length === 0)}
      testId="security-dialog"
    >
      {q.isLoading ? <LoadingState what="zabezpečení" /> : null}
      {q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : null}
      {forced ? (
        <p className="note bad" role="alert">
          Správce vyžaduje dvoufázové přihlášení. Zapněte ho (aplikace s kódy nebo passkey), jinak
          se k ostatním částem administrace nedostanete.
        </p>
      ) : null}
      {err ? <ErrorState error={err} /> : null}
      {note ? (
        <p className="note" role="status">
          {note}
        </p>
      ) : null}

      {codes ? (
        <div className="confirm" data-testid="backup-codes">
          <b>Záložní kódy (zobrazí se jen teď, uložte je na bezpečné místo):</b>
          <pre>{codes.join('\n')}</pre>
          <p className="muted">
            Každý kód jde použít jednou, když nemáte telefon. Staré kódy přestaly platit.
          </p>
          <button type="button" className="btn" onClick={() => setCodes(null)}>
            Uložil jsem je
          </button>
        </div>
      ) : null}

      {d ? (
        <>
          <h3 className="s2c-sub">Aplikace s kódy (Google Authenticator, 1Password …)</h3>
          {d.totp ? (
            <>
              <p>
                <StatusBadge tone="ok">zapnuto</StatusBadge> Zbývá {d.backupCodesLeft} záložních
                kódů.
              </p>
              <div className="s2a-filters">
                <label className="field">
                  <span>Heslo (pro vypnutí nebo nové záložní kódy)</span>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                </label>
                <button
                  type="button"
                  className="btn"
                  disabled={!password}
                  onClick={() => regen.mutate()}
                >
                  Nové záložní kódy
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={!password || forced === true}
                  onClick={() => disable.mutate()}
                >
                  Vypnout
                </button>
              </div>
            </>
          ) : setup ? (
            <div data-testid="totp-setup">
              <p>
                Naskenujte QR kód aplikací, nebo opište klíč, a zadejte šestimístný kód z aplikace.
              </p>
              <img
                src={setup.qrDataUrl}
                alt="QR kód pro aplikaci s kódy"
                width={220}
                height={220}
              />
              <p>
                Klíč: <code>{setup.secret}</code>
              </p>
              <div className="s2a-filters">
                <label className="field">
                  <span>Kód z aplikace</span>
                  <input
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    maxLength={7}
                  />
                </label>
                <button
                  type="button"
                  className="btn primary"
                  disabled={code.trim().length < 6}
                  onClick={() => enable.mutate()}
                >
                  Potvrdit a zapnout
                </button>
              </div>
            </div>
          ) : (
            <p>
              <StatusBadge tone="warn">vypnuto</StatusBadge>{' '}
              <button type="button" className="btn primary" onClick={() => start.mutate()}>
                Zapnout dvoufázové přihlášení
              </button>
            </p>
          )}

          <h3 className="s2c-sub">Passkey (otisk prstu, Face ID, bezpečnostní klíč)</h3>
          <p className="muted">
            Passkey umí přihlášení místo hesla a platí jako plný druhý faktor.
          </p>
          {d.passkeys.length > 0 ? (
            <table data-testid="passkeys">
              <thead>
                <tr>
                  <th>Název</th>
                  <th>Přidán</th>
                  <th>Naposledy použit</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {d.passkeys.map((k) => (
                  <tr key={k.id}>
                    <td>{k.name}</td>
                    <td>{dateTime(k.createdAt)}</td>
                    <td>{k.lastUsedAt ? dateTime(k.lastUsedAt) : '—'}</td>
                    <td>
                      <button
                        type="button"
                        className="btn"
                        onClick={() => {
                          const n = window.prompt('Nový název passkey', k.name);
                          if (n && n.trim()) rename.mutate({ id: k.id, name: n.trim() });
                        }}
                      >
                        Přejmenovat
                      </button>{' '}
                      <button type="button" className="btn" onClick={() => remove.mutate(k.id)}>
                        Smazat
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          <div className="s2a-filters">
            <label className="field">
              <span>Název nového passkey</span>
              <input
                value={name}
                maxLength={60}
                placeholder="např. Telefon"
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <button
              type="button"
              className="btn primary"
              disabled={addKey.isPending}
              onClick={() => addKey.mutate()}
            >
              Přidat passkey
            </button>
          </div>

          {d.isAdmin ? (
            <>
              <h3 className="s2c-sub">Pro všechny uživatele</h3>
              <label>
                <input
                  type="checkbox"
                  checked={d.require2faAll}
                  disabled={!d.totp && d.passkeys.length === 0}
                  onChange={(e) => require2fa.mutate(e.target.checked)}
                />{' '}
                Vynutit dvoufázové přihlášení pro všechny (nejdřív si ho zapněte sami; kdo ho nemá,
                smí jen do tohoto okna)
              </label>
            </>
          ) : null}
        </>
      ) : null}
    </Dialog>
  );
}
